import { useMemo, useState, type KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRanking } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { Chart } from '@/components/Chart';
import { CardSkeleton, ErrorState, SectionHead, StatLine } from '@/components/ui';
import { GrowthTag } from '@/components/GrowthTag';
import { IconArrowUp, IconFlame, IconTrophy } from '@/components/Icons';
import { DepositRanking } from '@/components/ranking/DepositRanking';
import { LeaderboardList, memberItem, Podium, splitPodium } from '@/components/ranking/Leaderboard';
import { RankingTable } from '@/components/ranking/RankingTable';
import { formatMetric, receiptsLabel as receipts } from '@/components/ranking/rankingFormat';
import {
  formatBRL, formatBRLShort, formatDate, formatNumber, formatRange, LEVERAGE_LABELS, LEVERAGE_LONG_LABELS, PERIOD_LABELS, todayISO,
} from '@/lib/format';
import { RECEIPT_SOURCE_LABELS, RECEIPT_TYPES } from '@/lib/receipts';
import type { LeverageType, PeriodKey, Ranking, RankingMetric, ReceiptSource } from '@/lib/types';

const PERIODS: PeriodKey[] = ['today', 'yesterday', 'week', 'month', 'lastMonth', 'quarter', 'year', 'all'];

/**
 * A ordem acompanha a lista de tipos: cada tipo pelo seu valor, e depois
 * quantidade e ticket. Os rótulos curtos dos selos: com os por extenso, o
 * select alargava e a troca Ranking/Tabela caía sozinha numa segunda linha.
 */
const METRICS: { value: RankingMetric; label: string }[] = [
  { value: 'welcome', label: LEVERAGE_LABELS.WELCOME! },
  { value: 'event', label: LEVERAGE_LABELS.EVENT! },
  { value: 'sold', label: LEVERAGE_LABELS.SALE! },
  { value: 'live', label: LEVERAGE_LABELS.LIVE! },
  { value: 'deposit', label: 'Depositado (todos)' },
  { value: 'count', label: 'Nº de comprovantes' },
  { value: 'ticket', label: 'Ticket médio' },
];

/**
 * O tipo de cada ordem por tipo — escolher um puxa o outro. O depositado soma
 * o depósito e as alavancagens, então ordenar por ele não tira filtro nenhum.
 */
const METRIC_TYPE: Partial<Record<RankingMetric, LeverageType>> = {
  sold: 'SALE',
  welcome: 'WELCOME',
  event: 'EVENT',
  live: 'LIVE',
};
const TYPE_METRIC: Partial<Record<LeverageType, RankingMetric>> = {
  SALE: 'sold',
  WELCOME: 'welcome',
  EVENT: 'event',
  DEPOSIT: 'deposit',
  LIVE: 'live',
};

/** No filtro do ranking, a origem dita como recorte: "só isto". */
const SOURCE_FILTERS: { value: ReceiptSource; label: string }[] = [
  { value: 'EPHRA', label: 'Só vendas do checkout (Ephra)' },
  { value: 'CAKTO', label: 'Só vendas do checkout (Cakto)' },
  { value: 'KIRVANO', label: 'Só vendas do checkout (Kirvano)' },
  { value: 'DOTPAG', label: 'Só vendas do checkout (DotPag)' },
  { value: 'MUNDPAY', label: 'Só vendas do checkout (MundPay)' },
  { value: 'MANUAL', label: 'Só comprovantes anexados' },
  { value: 'CRYPTO', label: 'Só parcelas em cripto' },
];

/** O maior número de membros que a API devolve numa classificação. */
const ALL_MEMBERS_LIMIT = 200;

type View = 'board' | 'table';

/**
 * Quem está sendo classificado. Na URL (`?aba=depositos`), para dar para mandar
 * o link; o `?aba=clientes` de antes das duas visões abre os depósitos por cliente.
 */
type Ranked = 'membros' | 'depositos';
const RANKED_TABS: { value: Ranked; label: string }[] = [
  { value: 'membros', label: 'Membros' },
  { value: 'depositos', label: 'Depósitos' },
];

/**
 * Ranking: os membros pelos comprovantes e, na outra aba, os depósitos — a
 * contabilização do leilão, por membro do comercial e por cliente. Essa aba é
 * só dos administradores (decisão do dono, 29/10/2026): o membro não vê as
 * abas, e o `?aba=depositos` dele abre o ranking de membros. A API também recusa.
 */
export function RankingPage() {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get('aba');
  const ranked: Ranked = isAdmin && (tab === 'depositos' || tab === 'clientes') ? 'depositos' : 'membros';

  function choose(next: Ranked) {
    setParams(next === 'depositos' ? { aba: 'depositos' } : {}, { replace: true });
  }

  /** Setas trocam de aba, como pede o padrão de abas. */
  function onTabKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next = ranked === 'membros' ? 'depositos' : 'membros';
    choose(next);
    document.getElementById(`ranking-tab-${next}`)?.focus();
  }

  return (
    <>
      <h1 className="page-title">Ranking</h1>
      <p className="page-lede">
        {ranked === 'depositos'
          ? 'Quem trouxe mais depósito no período: os membros do comercial pelo depositado dos clientes deles (abra um membro para ver os clientes) e, em "Por cliente", os clientes que mais depositaram. Só os administradores veem esta aba: os membros do comercial não têm acesso.'
          : 'Quem mais vende no período, pelas vendas pagas no checkout pelo link de cada membro. Os comprovantes de venda que ainda contam ficam fora do valor, no "i" ao lado dele.'}
      </p>

      {isAdmin ? (
        <>
          <div className="tabs" role="tablist" aria-label="Classificar" onKeyDown={onTabKey}>
            {RANKED_TABS.map((item) => (
              <button
                key={item.value}
                type="button"
                role="tab"
                id={`ranking-tab-${item.value}`}
                aria-selected={ranked === item.value}
                aria-controls="ranking-panel"
                tabIndex={ranked === item.value ? 0 : -1}
                className="tab"
                onClick={() => choose(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div id="ranking-panel" role="tabpanel" aria-labelledby={`ranking-tab-${ranked}`}>
            {ranked === 'depositos' ? <DepositRanking /> : <MembersRanking />}
          </div>
        </>
      ) : (
        <MembersRanking />
      )}
    </>
  );
}

/**
 * O pódio e a classificação de todos os membros, inclusive quem ainda não
 * pontuou. Conta os comprovantes anexados e as vendas pagas no checkout pelo
 * link do membro, que entram sozinhas. O administrador filtra por origem e
 * tipo, e troca para a tabela com todos os números.
 */
function MembersRanking() {
  const { isAdmin } = useAuth();
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [metric, setMetric] = useState<RankingMetric>('sold');
  const [leverageType, setLeverageType] = useState('');
  const [source, setSource] = useState('');
  const [from, setFrom] = useState(todayISO().slice(0, 8) + '01');
  const [to, setTo] = useState(todayISO());
  const [view, setView] = useState<View>('board');

  const custom = period === 'custom';

  // Filtrar um tipo ordena pelo valor dele: filtrando o evento, ordenar pela venda daria zero a todos.
  function changeType(type: string) {
    setLeverageType(type);
    const matching = TYPE_METRIC[type as LeverageType];
    if (matching) setMetric(matching);
  }

  // E ordenar por um tipo com outro tipo filtrado tira o filtro, pelo mesmo motivo.
  function changeMetric(next: RankingMetric) {
    setMetric(next);
    const type = METRIC_TYPE[next];
    if (type && leverageType && leverageType !== type) setLeverageType('');
  }
  const { data, isLoading, error } = useRanking({
    period,
    metric,
    leverageType: leverageType || undefined,
    source: source || undefined,
    ...(custom ? { from, to } : {}),
    includeZero: 'true',
    limit: ALL_MEMBERS_LIMIT,
  });

  const series = data?.dailySeries ?? [];
  const chartLabels = useMemo(() => series.map((point) => formatDate(point.date).slice(0, 5)), [series]);
  const chartValues = useMemo(() => series.map((point) => point.total), [series]);

  if (error) return <ErrorState message={(error as Error).message} />;

  const entries = data?.entries ?? [];
  const items = entries.map((entry) => memberItem(entry, metric));
  const previousPeriod = data?.previousPeriod ? formatRange(data.previousPeriod.from, data.previousPeriod.to) : null;
  const { podium, rest } = splitPodium(items);
  const filterLabel = [
    data?.leverageType ? LEVERAGE_LABELS[data.leverageType] : null,
    data?.source ? RECEIPT_SOURCE_LABELS[data.source] : null,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <div className="toolbar">
        <select className="inp" value={period} onChange={(event) => setPeriod(event.target.value as PeriodKey)} aria-label="Período">
          {PERIODS.map((option) => <option key={option} value={option}>{PERIOD_LABELS[option]}</option>)}
          <option value="custom">Personalizado</option>
        </select>

        {custom ? (
          <>
            <input className="inp" style={{ width: 150 }} type="date" value={from} onChange={(event) => setFrom(event.target.value)} aria-label="Data inicial" />
            <span className="muted" style={{ fontSize: 12 }}>até</span>
            <input className="inp" style={{ width: 150 }} type="date" value={to} onChange={(event) => setTo(event.target.value)} aria-label="Data final" />
          </>
        ) : null}

        <select className="inp" value={metric} onChange={(event) => changeMetric(event.target.value as RankingMetric)} aria-label="Ordenar por">
          {METRICS.map((option) => <option key={option.value} value={option.value}>Ordenar: {option.label}</option>)}
        </select>

        <select className="inp" value={leverageType} onChange={(event) => changeType(event.target.value)} aria-label="Tipo">
          <option value="">Todos os tipos</option>
          {RECEIPT_TYPES.map((type) => <option key={type} value={type}>{LEVERAGE_LONG_LABELS[type]}</option>)}
        </select>

        <select className="inp" value={source} onChange={(event) => setSource(event.target.value)} aria-label="Origem">
          <option value="">Checkout e comprovantes</option>
          {SOURCE_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>

        {isAdmin ? (
          <div className="view-switch" role="group" aria-label="Visão" style={{ marginLeft: 'auto' }}>
            <button type="button" aria-pressed={view === 'board'} onClick={() => setView('board')}>Ranking</button>
            <button type="button" aria-pressed={view === 'table'} onClick={() => setView('table')}>Tabela</button>
          </div>
        ) : null}
      </div>

      {isLoading || !data ? (
        <div className="grid grid3"><CardSkeleton height={220} /><CardSkeleton height={260} /><CardSkeleton height={200} /></div>
      ) : (
        <>
          <Totals data={data} filterLabel={filterLabel} />

          {podium.length === 0 ? (
            <NoScore data={data} period={period} onPeriod={setPeriod} />
          ) : view === 'table' ? (
            <RankingTable entries={entries} metric={metric} />
          ) : (
            <>
              <Podium items={podium} previousPeriod={previousPeriod} withInfo={isAdmin} />
              {rest.length > 0 ? (
                <section>
                  <SectionHead title={`Classificação · ${entries.length} ${entries.length === 1 ? 'membro' : 'membros'}`} />
                  <LeaderboardList items={rest} leaderScore={podium[0]?.score ?? 0} previousPeriod={previousPeriod} withInfo={isAdmin} />
                </section>
              ) : null}
            </>
          )}

          {podium.length > 0 ? <Lower data={data} metric={metric} labels={chartLabels} values={chartValues} /> : null}
        </>
      )}
    </>
  );
}

function Totals({ data, filterLabel }: { data: Ranking; filterLabel: string }) {
  const { totals } = data;
  return (
    <div className="metric-row cols-5" style={{ marginBottom: 34 }}>
      <StatLine
        label={`Vendido · ${data.period.label}${filterLabel ? ` · ${filterLabel}` : ''}`}
        value={formatBRLShort(totals.soldTotal)}
        color="var(--emphasis)"
        sub={<GrowthTag growth={totals.growth} previousLabel={formatBRL(totals.previousSoldTotal)} />}
      />
      <StatLine
        label="Pelo checkout"
        value={formatBRLShort(totals.checkoutTotal)}
        sub={`${formatNumber(totals.checkoutCount)} ${totals.checkoutCount === 1 ? 'venda paga pelo link' : 'vendas pagas pelo link'}`}
      />
      <StatLine label="Comprovantes" value={formatNumber(totals.receiptCount)} sub={`Ticket médio ${formatBRLShort(totals.averageTicket)}`} />
      <StatLine
        label="Depositado"
        value={formatBRLShort(totals.depositTotal)}
        sub={`Buscada ${formatBRLShort(totals.leverageTotal)} · repasse ${formatBRLShort(totals.transferTotal)}`}
      />
      <StatLine label="Membros pontuando" value={`${totals.rankedMembers}/${totals.activeMembers}`} />
    </div>
  );
}

function NoScore({ data, period, onPeriod }: { data: Ranking; period: PeriodKey | 'custom'; onPeriod: (period: PeriodKey) => void }) {
  return (
    <div style={{ textAlign: 'center', padding: '34px 20px' }}>
      <div className="muted" style={{ fontSize: 13 }}>
        Ninguém pontuou em <strong>{data.period.label.toLowerCase()}</strong> com esses filtros.
      </div>
      {period !== 'all' && period !== 'year' ? (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 14 }}>
          <button type="button" className="btn btn-sm" onClick={() => onPeriod('year')}>Ver o ano inteiro</button>
          <button type="button" className="btn btn-sm" onClick={() => onPeriod('all')}>Ver todo o período</button>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <Link to="/comprovantes" className="btn btn-sm btn-primary">Anexar um comprovante</Link>
        </div>
      )}
    </div>
  );
}

/** Gráfico do vendido por dia e os destaques, lado a lado quando cabe. */
function Lower({ data, metric, labels, values }: { data: Ranking; metric: RankingMetric; labels: string[]; values: number[] }) {
  const { leader, bestDay, biggestSale } = data.highlights;
  return (
    <div className="ranking-lower">
      <section>
        <SectionHead title={`Vendido por dia — ${data.period.label}`} />
        {values.length > 1 ? (
          <Chart type="line" labels={labels} data={values} height={200} ariaLabel="Gráfico do valor vendido por dia no período selecionado" />
        ) : (
          <p className="muted" style={{ fontSize: 12.5 }}>Com um dia só de vendas não há linha para desenhar.</p>
        )}
      </section>
      <section>
        <SectionHead title="Destaques do período" />
        <div className="card ranking-highlights">
          <Highlight
            icon={<IconTrophy size={14} />}
            label={`Líder em ${data.metricLabel.toLowerCase()}`}
            value={leader ? leader.member.name : '—'}
            detail={leader ? formatMetric(metric, leader.score) : undefined}
          />
          <Highlight
            icon={<IconFlame />}
            label="Melhor dia"
            value={bestDay ? formatDate(bestDay.date) : '—'}
            detail={bestDay ? `${formatBRL(bestDay.total)} em ${receipts(bestDay.count)}` : undefined}
          />
          <Highlight
            icon={<IconArrowUp />}
            label="Maior venda"
            value={biggestSale ? formatBRL(biggestSale.amount) : '—'}
            detail={biggestSale ? `${biggestSale.memberName} · ${formatDate(biggestSale.date)} ${biggestSale.time}` : undefined}
          />
        </div>
      </section>
    </div>
  );
}

function Highlight({ icon, label, value, detail }: { icon: JSX.Element; label: string; value: string; detail?: string }) {
  return (
    <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
      <span className="emphasis" style={{ marginTop: 2 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div className="muted" style={{ fontSize: 11.5 }}>{label}</div>
        <div className="num" style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
        {detail ? <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{detail}</div> : null}
      </div>
    </div>
  );
}
